import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  BuildingOfficeIcon,
  UsersIcon,
  Cog6ToothIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  CloudArrowUpIcon,
  ShieldCheckIcon,
  SparklesIcon,
  CreditCardIcon,
  CpuChipIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import platformAdminApi, {
  PlatformStatus,
  YoutubeCookiesStatus,
  FeatureFlag,
  FeatureFlagKey,
  AdminPlan,
  AIModelSlotInfo,
  AIModelSlot,
} from '@/services/platformAdminApi';

type Tab = 'dashboard' | 'organizations' | 'users' | 'settings' | 'feature-flags' | 'ai-models';

const SuperAdminPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('dashboard');
  const [status, setStatus] = useState<PlatformStatus | null>(null);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [cookiesStatus, setCookiesStatus] = useState<YoutubeCookiesStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [cookiesText, setCookiesText] = useState('');
  const [cookiesFile, setCookiesFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([]);
  const [togglingKey, setTogglingKey] = useState<FeatureFlagKey | null>(null);
  const [newsroomEnabledMap, setNewsroomEnabledMap] = useState<Record<string, boolean>>({});
  const [togglingOrgId, setTogglingOrgId] = useState<string | null>(null);
  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [changingPlanOrgId, setChangingPlanOrgId] = useState<string | null>(null);
  const [aiModelSlots, setAiModelSlots] = useState<AIModelSlotInfo[]>([]);
  const [savingDefaultSlot, setSavingDefaultSlot] = useState<AIModelSlot | null>(null);
  const [bulkModelBySlot, setBulkModelBySlot] = useState<Record<string, string>>({});
  const [selectedOrgsBySlot, setSelectedOrgsBySlot] = useState<Record<string, string[]>>({});
  const [applyingBulkSlot, setApplyingBulkSlot] = useState<AIModelSlot | null>(null);
  const [clearingOverride, setClearingOverride] = useState<string | null>(null); // `${slot}:${organizationId}`

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    if (activeTab === 'organizations' && organizations.length === 0) loadOrgs();
    if (activeTab === 'users' && users.length === 0) loadUsers();
    if (activeTab === 'settings') loadCookiesStatus();
    if (activeTab === 'feature-flags') loadFeatureFlags();
    if (activeTab === 'ai-models') {
      loadAIModels();
      if (organizations.length === 0) loadOrgs();
    }
  }, [activeTab]);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const res = await platformAdminApi.getStatus();
      if (res.data.success) setStatus(res.data.data);
    } catch {
      toast.error('Failed to load platform status');
    } finally {
      setLoading(false);
    }
  };

  const loadOrgs = async () => {
    try {
      const res = await platformAdminApi.getOrganizations();
      if (res.data.success) {
        setOrganizations(res.data.data);
        loadNewsroomStatuses(res.data.data);
      }
      if (plans.length === 0) {
        const plansRes = await platformAdminApi.getPlans();
        if (plansRes.data.success) setPlans(plansRes.data.data);
      }
    } catch {
      toast.error('Failed to load organizations');
    }
  };

  const handleChangeOrgPlan = async (organizationId: string, plan: string) => {
    setChangingPlanOrgId(organizationId);
    try {
      await platformAdminApi.setOrganizationPlan(organizationId, plan);
      setOrganizations((prev) => prev.map((org) => (org._id === organizationId ? { ...org, subscription: { ...org.subscription, plan } } : org)));
      toast.success('Organization plan updated');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to update organization plan');
    } finally {
      setChangingPlanOrgId(null);
    }
  };

  const loadNewsroomStatuses = async (orgs: any[]) => {
    try {
      const results = await Promise.all(
        orgs.map((org) =>
          platformAdminApi
            .getOrgFeature(org._id, 'newsroom')
            .then((res) => [org._id, res.data.data.enabled] as const)
            .catch(() => [org._id, false] as const)
        )
      );
      setNewsroomEnabledMap(Object.fromEntries(results));
    } catch {
      // Non-fatal — the toggle just won't show a reliable initial state.
    }
  };

  const handleToggleNewsroom = async (organizationId: string, enabled: boolean) => {
    setTogglingOrgId(organizationId);
    try {
      await platformAdminApi.updateOrgFeature(organizationId, 'newsroom', enabled);
      setNewsroomEnabledMap((prev) => ({ ...prev, [organizationId]: enabled }));
      toast.success(`Newsroom ${enabled ? 'enabled' : 'disabled'} for this organization`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to update Newsroom access');
    } finally {
      setTogglingOrgId(null);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await platformAdminApi.getUsers();
      if (res.data.success) setUsers(res.data.data);
    } catch {
      toast.error('Failed to load users');
    }
  };

  const loadCookiesStatus = async () => {
    try {
      const res = await platformAdminApi.getYoutubeCookiesStatus();
      if (res.data.success) setCookiesStatus(res.data.data);
    } catch {
      toast.error('Failed to check cookie status');
    }
  };

  const loadFeatureFlags = async () => {
    try {
      const res = await platformAdminApi.getFeatureFlags();
      if (res.data.success) setFeatureFlags(res.data.data);
    } catch {
      toast.error('Failed to load feature flags');
    }
  };

  const handleToggleFlag = async (key: FeatureFlagKey, enabled: boolean) => {
    setTogglingKey(key);
    try {
      const res = await platformAdminApi.updateFeatureFlag(key, enabled);
      if (res.data.success) {
        setFeatureFlags(prev => prev.map(f => (f.key === key ? res.data.data : f)));
        toast.success(`${res.data.data.label} ${enabled ? 'enabled' : 'disabled'}`);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to update feature flag');
    } finally {
      setTogglingKey(null);
    }
  };

  const loadAIModels = async () => {
    try {
      const res = await platformAdminApi.getAIModels();
      if (res.data.success) setAiModelSlots(res.data.data);
    } catch {
      toast.error('Failed to load AI model settings');
    }
  };

  const handleSetPlatformDefault = async (slot: AIModelSlot, modelId: string) => {
    setSavingDefaultSlot(slot);
    try {
      await platformAdminApi.setAIModelPlatformDefault(slot, modelId);
      setAiModelSlots((prev) => prev.map((s) => (s.slot === slot ? { ...s, platformDefaultModelId: modelId } : s)));
      toast.success('Platform default model updated for all organizations without an override');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to update platform default model');
    } finally {
      setSavingDefaultSlot(null);
    }
  };

  const toggleOrgSelection = (slot: AIModelSlot, organizationId: string) => {
    setSelectedOrgsBySlot((prev) => {
      const current = prev[slot] || [];
      const next = current.includes(organizationId)
        ? current.filter((id) => id !== organizationId)
        : [...current, organizationId];
      return { ...prev, [slot]: next };
    });
  };

  const handleApplyToSelectedOrgs = async (slot: AIModelSlot) => {
    const organizationIds = selectedOrgsBySlot[slot] || [];
    const modelId = bulkModelBySlot[slot];
    if (organizationIds.length === 0) {
      toast.error('Select at least one organization first');
      return;
    }
    if (!modelId) {
      toast.error('Select a model first');
      return;
    }
    setApplyingBulkSlot(slot);
    try {
      await platformAdminApi.setAIModelOrgOverrides(slot, organizationIds, modelId);
      await loadAIModels();
      setSelectedOrgsBySlot((prev) => ({ ...prev, [slot]: [] }));
      toast.success(`${organizationIds.length} organization(s) pinned to this model`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to apply model to selected organizations');
    } finally {
      setApplyingBulkSlot(null);
    }
  };

  const handleClearOverride = async (slot: AIModelSlot, organizationId: string) => {
    const key = `${slot}:${organizationId}`;
    setClearingOverride(key);
    try {
      await platformAdminApi.setAIModelOrgOverrides(slot, [organizationId], null);
      setAiModelSlots((prev) =>
        prev.map((s) => (s.slot === slot ? { ...s, orgOverrides: s.orgOverrides.filter((o) => o.organizationId !== organizationId) } : s))
      );
      toast.success('Override cleared — organization now follows the platform default');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to clear override');
    } finally {
      setClearingOverride(null);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCookiesFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setCookiesText((ev.target?.result as string) || '');
    reader.readAsText(file);
  };

  const handleUploadCookies = async () => {
    if (!cookiesText.trim()) {
      toast.error('Please paste or upload a Netscape cookie file first');
      return;
    }
    setUploading(true);
    try {
      const res = await platformAdminApi.updateYoutubeCookies(cookiesText);
      if (res.data.success) {
        toast.success('YouTube cookies updated successfully in AWS Secrets Manager');
        setCookiesText('');
        setCookiesFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        loadCookiesStatus();
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to update cookies');
    } finally {
      setUploading(false);
    }
  };

  const tabs: { id: Tab; label: string; icon: React.ElementType }[] = [
    { id: 'dashboard', label: 'Dashboard', icon: ShieldCheckIcon },
    { id: 'organizations', label: 'Organizations', icon: BuildingOfficeIcon },
    { id: 'users', label: 'Users', icon: UsersIcon },
    { id: 'feature-flags', label: 'Feature Flags', icon: SparklesIcon },
    { id: 'ai-models', label: 'AI Models', icon: CpuChipIcon },
    { id: 'settings', label: 'Platform Settings', icon: Cog6ToothIcon },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-purple-600 flex items-center justify-center">
          <ShieldCheckIcon className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Platform Admin</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Super administrator control panel</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-1 -mb-px">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-purple-600 text-purple-600 dark:text-purple-400 dark:border-purple-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
          <Link
            to="/app/superadmin/plans"
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            <CreditCardIcon className="h-4 w-4" />
            Plans
          </Link>
        </nav>
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <ArrowPathIcon className="h-8 w-8 animate-spin text-purple-600" />
            </div>
          ) : status ? (
            <>
              {/* Stat cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { label: 'Total Organizations', value: status.stats.totalOrgs, icon: BuildingOfficeIcon, color: 'bg-blue-500' },
                  { label: 'Active Organizations', value: status.stats.activeOrgs, icon: CheckCircleIcon, color: 'bg-green-500' },
                  { label: 'Total Users', value: status.stats.totalUsers, icon: UsersIcon, color: 'bg-purple-500' },
                ].map((card) => (
                  <div key={card.label} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
                    <div className="flex items-center gap-3">
                      <div className={`h-10 w-10 rounded-lg ${card.color} flex items-center justify-center`}>
                        <card.icon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{card.value}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{card.label}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Integration status */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
                <h2 className="font-semibold text-gray-900 dark:text-white mb-4">Integration Status</h2>
                <div className="flex items-center gap-3">
                  {status.integrations.youtubeCookies === 'configured' ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                  ) : (
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
                  )}
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    YouTube Cookies —{' '}
                    <span className={status.integrations.youtubeCookies === 'configured' ? 'text-green-600 font-medium' : 'text-yellow-600 font-medium'}>
                      {status.integrations.youtubeCookies === 'configured' ? 'Configured' : 'Not configured'}
                    </span>
                  </span>
                  {status.integrations.youtubeCookies !== 'configured' && (
                    <button
                      onClick={() => setActiveTab('settings')}
                      className="ml-auto text-xs text-purple-600 hover:text-purple-700 font-medium"
                    >
                      Configure →
                    </button>
                  )}
                </div>
              </div>

              {/* Recent tables */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
                  <h2 className="font-semibold text-gray-900 dark:text-white mb-3">Recent Organizations</h2>
                  <div className="space-y-2">
                    {status.recentOrgs.map((org: any) => (
                      <div key={org._id} className="flex items-center justify-between text-sm">
                        <span className="text-gray-800 dark:text-gray-200 font-medium">{org.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${org.isActive ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' : 'bg-red-100 text-red-700'}`}>
                          {org.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
                  <h2 className="font-semibold text-gray-900 dark:text-white mb-3">Recent Users</h2>
                  <div className="space-y-2">
                    {status.recentUsers.map((u: any) => (
                      <div key={u._id} className="flex items-center justify-between text-sm">
                        <div>
                          <span className="text-gray-800 dark:text-gray-200 font-medium">{u.name}</span>
                          <span className="ml-2 text-gray-400 text-xs">{u.email}</span>
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{u.role}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* Organizations Tab */}
      {activeTab === 'organizations' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h2 className="font-semibold text-gray-900 dark:text-white">All Organizations ({organizations.length})</h2>
            <button onClick={loadOrgs} className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1">
              <ArrowPathIcon className="h-4 w-4" /> Refresh
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  {['Name', 'Country', 'Plan', 'Status', 'Newsroom', 'Created'].map((h) => (
                    <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {organizations.map((org) => (
                  <tr key={org._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-5 py-3 font-medium text-gray-900 dark:text-white">{org.name}</td>
                    <td className="px-5 py-3 text-gray-500 dark:text-gray-400">{org.country || '—'}</td>
                    <td className="px-5 py-3">
                      <select
                        value={org.subscription?.plan || ''}
                        onChange={(e) => handleChangeOrgPlan(org._id, e.target.value)}
                        disabled={changingPlanOrgId === org._id}
                        className="text-xs border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-md px-2 py-1 disabled:opacity-50"
                      >
                        {!plans.some((p) => p.key === org.subscription?.plan) && org.subscription?.plan && (
                          <option value={org.subscription.plan}>{org.subscription.plan}</option>
                        )}
                        {plans.map((p) => (
                          <option key={p.key} value={p.key}>{p.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${org.isActive ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' : 'bg-red-100 text-red-700'}`}>
                        {org.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <button
                        onClick={() => handleToggleNewsroom(org._id, !newsroomEnabledMap[org._id])}
                        disabled={togglingOrgId === org._id}
                        title={newsroomEnabledMap[org._id] ? 'Disable Newsroom for this org' : 'Enable Newsroom for this org'}
                        className={`text-xs px-2 py-0.5 rounded-full font-medium transition-colors disabled:opacity-50 ${
                          newsroomEnabledMap[org._id]
                            ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                            : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                        }`}
                      >
                        {togglingOrgId === org._id ? '…' : newsroomEnabledMap[org._id] ? 'Enabled' : 'Disabled'}
                      </button>
                    </td>
                    <td className="px-5 py-3 text-gray-500 dark:text-gray-400">{new Date(org.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {organizations.length === 0 && (
              <p className="text-center text-gray-400 py-8 text-sm">No organizations found</p>
            )}
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h2 className="font-semibold text-gray-900 dark:text-white">All Users ({users.length})</h2>
            <button onClick={loadUsers} className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1">
              <ArrowPathIcon className="h-4 w-4" /> Refresh
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  {['Name', 'Email', 'Role', 'Status', 'Verified', 'Last Login'].map((h) => (
                    <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {users.map((u) => (
                  <tr key={u._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-5 py-3 font-medium text-gray-900 dark:text-white">{u.name}</td>
                    <td className="px-5 py-3 text-gray-500 dark:text-gray-400">{u.email}</td>
                    <td className="px-5 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.role === 'SuperAdmin' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.isActive ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' : 'bg-red-100 text-red-700'}`}>
                        {u.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      {u.isEmailVerified ? (
                        <CheckCircleIcon className="h-4 w-4 text-green-500" />
                      ) : (
                        <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />
                      )}
                    </td>
                    <td className="px-5 py-3 text-gray-400 text-xs">
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users.length === 0 && (
              <p className="text-center text-gray-400 py-8 text-sm">No users found</p>
            )}
          </div>
        </div>
      )}

      {/* Feature Flags Tab */}
      {activeTab === 'feature-flags' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden max-w-3xl">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="font-semibold text-gray-900 dark:text-white">Platform Feature Flags</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Features are hidden from every organization until turned on here. Toggling takes effect immediately, no deploy required.
            </p>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {featureFlags.map((flag) => (
              <div key={flag.key} className="flex items-center justify-between gap-4 px-5 py-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{flag.label}</p>
                  {flag.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{flag.description}</p>
                  )}
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={flag.enabled}
                  disabled={togglingKey === flag.key}
                  onClick={() => handleToggleFlag(flag.key, !flag.enabled)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                    flag.enabled ? 'bg-purple-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      flag.enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            ))}
            {featureFlags.length === 0 && (
              <p className="text-center text-gray-400 py-8 text-sm">No feature flags found</p>
            )}
          </div>
        </div>
      )}

      {/* AI Models Tab */}
      {activeTab === 'ai-models' && (
        <div className="space-y-6 max-w-4xl">
          {aiModelSlots.map((slotInfo) => {
            const selectedOrgs = selectedOrgsBySlot[slotInfo.slot] || [];
            const bulkModel = bulkModelBySlot[slotInfo.slot] || '';
            const pinnedOrgIds = new Set(slotInfo.orgOverrides.map((o) => o.organizationId));
            const availableOrgs = organizations.filter((org) => !pinnedOrgIds.has(org._id));

            return (
              <div key={slotInfo.slot} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="font-semibold text-gray-900 dark:text-white">{slotInfo.label}</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{slotInfo.description}</p>
                </div>

                {/* Platform default */}
                <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">Platform default</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    Applies to every organization that has no model override of its own. Takes effect immediately for new jobs, no deploy required.
                  </p>
                  <div className="flex items-center gap-3">
                    <select
                      value={slotInfo.platformDefaultModelId}
                      onChange={(e) => handleSetPlatformDefault(slotInfo.slot, e.target.value)}
                      disabled={savingDefaultSlot === slotInfo.slot}
                      className="flex-1 max-w-sm text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-2 disabled:opacity-50"
                    >
                      {slotInfo.options.map((o) => (
                        <option key={o.id} value={o.id}>{o.label} — {o.languages}, {o.latency}</option>
                      ))}
                    </select>
                    {savingDefaultSlot === slotInfo.slot && <ArrowPathIcon className="h-4 w-4 animate-spin text-gray-400" />}
                  </div>
                  {(() => {
                    const current = slotInfo.options.find((o) => o.id === slotInfo.platformDefaultModelId);
                    return current ? (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{current.description}</p>
                    ) : null;
                  })()}
                </div>

                {/* Existing org overrides */}
                {slotInfo.orgOverrides.length > 0 && (
                  <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-900 dark:text-white mb-3">Organization overrides ({slotInfo.orgOverrides.length})</p>
                    <div className="space-y-2">
                      {slotInfo.orgOverrides.map((override) => {
                        const key = `${slotInfo.slot}:${override.organizationId}`;
                        const option = slotInfo.options.find((o) => o.id === override.modelId);
                        return (
                          <div key={override.organizationId} className="flex items-center justify-between gap-3 text-sm bg-gray-50 dark:bg-gray-700/40 rounded-lg px-3 py-2">
                            <div className="min-w-0">
                              <span className="font-medium text-gray-900 dark:text-white">{override.organizationName}</span>
                              <span className="text-gray-500 dark:text-gray-400"> — pinned to </span>
                              <span className="text-purple-600 dark:text-purple-400 font-medium">{option?.label || override.modelId}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleClearOverride(slotInfo.slot, override.organizationId)}
                              disabled={clearingOverride === key}
                              title="Revert to platform default"
                              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50 flex-shrink-0"
                            >
                              <XMarkIcon className="h-3.5 w-3.5" />
                              Clear
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Apply to specific organizations */}
                <div className="px-5 py-4">
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">Pin specific organizations to a model</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    Select one or more organizations to upgrade (or pin to an older model) independently of the platform default above.
                  </p>

                  <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700 mb-3">
                    {availableOrgs.length === 0 ? (
                      <p className="text-center text-gray-400 py-4 text-xs">
                        {organizations.length === 0 ? 'Loading organizations…' : 'Every organization is already pinned above.'}
                      </p>
                    ) : (
                      availableOrgs.map((org) => (
                        <label key={org._id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <input
                            type="checkbox"
                            checked={selectedOrgs.includes(org._id)}
                            onChange={() => toggleOrgSelection(slotInfo.slot, org._id)}
                            className="rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500"
                          />
                          <span className="text-gray-800 dark:text-gray-200">{org.name}</span>
                        </label>
                      ))
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    <select
                      value={bulkModel}
                      onChange={(e) => setBulkModelBySlot((prev) => ({ ...prev, [slotInfo.slot]: e.target.value }))}
                      className="flex-1 max-w-sm text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-2"
                    >
                      <option value="">Select a model…</option>
                      {slotInfo.options.map((o) => (
                        <option key={o.id} value={o.id}>{o.label}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => handleApplyToSelectedOrgs(slotInfo.slot)}
                      disabled={applyingBulkSlot === slotInfo.slot || selectedOrgs.length === 0 || !bulkModel}
                      className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-md transition-colors"
                    >
                      {applyingBulkSlot === slotInfo.slot ? 'Applying…' : `Apply to ${selectedOrgs.length || ''} selected`.trim()}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {aiModelSlots.length === 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
              <p className="text-center text-gray-400 text-sm">Loading AI model settings…</p>
            </div>
          )}
        </div>
      )}

      {/* Platform Settings Tab */}
      {activeTab === 'settings' && (
        <div className="space-y-6 max-w-2xl">
          {/* YouTube Cookies Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-start gap-3 mb-5">
              <div className="h-10 w-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                <CloudArrowUpIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900 dark:text-white">YouTube Cookie Authentication</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  Upload a Netscape-format cookie file exported from a logged-in YouTube session. Stored securely in AWS Secrets Manager.
                </p>
              </div>
            </div>

            {/* Current Status */}
            {cookiesStatus && (
              <div className={`flex items-center gap-2 p-3 rounded-lg mb-5 text-sm ${
                cookiesStatus.status === 'configured'
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                  : 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300'
              }`}>
                {cookiesStatus.status === 'configured' ? (
                  <CheckCircleIcon className="h-4 w-4 flex-shrink-0" />
                ) : (
                  <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                )}
                <span>
                  {cookiesStatus.status === 'configured'
                    ? `Cookies configured — last updated ${cookiesStatus.lastChangedDate ? new Date(cookiesStatus.lastChangedDate).toLocaleString() : 'unknown'}`
                    : 'No cookies configured — YouTube downloads may fail without authentication'}
                </span>
              </div>
            )}

            {/* How to export */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-5 text-xs text-gray-600 dark:text-gray-400 space-y-1">
              <p className="font-semibold text-gray-700 dark:text-gray-300 mb-1">How to export YouTube cookies:</p>
              <p>1. Install <span className="font-medium">Get cookies.txt LOCALLY</span> Chrome extension</p>
              <p>2. Log into YouTube with a dedicated Google account</p>
              <p>3. Click the extension icon → Export for <span className="font-mono">youtube.com</span></p>
              <p>4. Upload the <span className="font-mono">.txt</span> file below or paste the content</p>
            </div>

            {/* File upload */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Upload cookie file (.txt)
                </label>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center cursor-pointer hover:border-purple-400 dark:hover:border-purple-500 transition-colors"
                >
                  <CloudArrowUpIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {cookiesFile ? (
                      <span className="text-green-600 font-medium">{cookiesFile.name} selected</span>
                    ) : (
                      <>Click to select a cookies.txt file</>
                    )}
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".txt,text/plain"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Or paste cookie content directly
                </label>
                <textarea
                  value={cookiesText}
                  onChange={(e) => setCookiesText(e.target.value)}
                  rows={8}
                  placeholder="# Netscape HTTP Cookie File&#10;# https://curl.haxx.se/rfc/cookie_spec.html&#10;.youtube.com	TRUE	/	TRUE	..."
                  className="w-full font-mono text-xs bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg p-3 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                />
              </div>

              <button
                onClick={handleUploadCookies}
                disabled={uploading || !cookiesText.trim()}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors text-sm"
              >
                {uploading ? (
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                ) : (
                  <CloudArrowUpIcon className="h-4 w-4" />
                )}
                {uploading ? 'Saving to AWS Secrets Manager...' : 'Save Cookies to AWS Secrets Manager'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminPage;
